import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
    Grid,
    Card,
    CardContent,
    CardHeader,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Autocomplete,
    Tooltip,
    Box,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    DialogContentText,
} from "@mui/material";
import {
    Edit,
    Add,
    Facebook,
    LinkedIn,
    Instagram,
    WhatsApp,
    Telegram,
    Person,
    Link as LinkIcon,
    ContentCopy,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import safeToast from "utils/safeToast";
import { API_ENDPOINTS } from "config/api";
import { decryptText } from "config/encryption";

// Custom X (formerly Twitter) icon component
const XIcon = ({ sx }) => (
    <Box
        component="svg"
        viewBox="0 0 24 24"
        sx={{
            width: 24,
            height: 24,
            fill: "currentColor",
            ...sx,
        }}
    >
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </Box>
);

XIcon.propTypes = {
    sx: PropTypes.object,
};

XIcon.defaultProps = {
    sx: {},
};

const iconMap = {
    Facebook: <Facebook sx={{ color: "#4267B2" }} />,
    X: <XIcon sx={{ color: "#000000" }} />,
    Twitter: <XIcon sx={{ color: "#1DA1F2" }} />, // Legacy support
    LinkedIn: <LinkedIn sx={{ color: "#0077B5" }} />,
    Instagram: <Instagram sx={{ color: "#E1306C" }} />,
    Whatsapp: <WhatsApp sx={{ color: "#25D366" }} />,
    WhatsApp: <WhatsApp sx={{ color: "#25D366" }} />,
    Telegram: <Telegram sx={{ color: "#0088cc" }} />,
};

const SocialMediaLinks = () => {
    const [socialLinks, setSocialLinks] = useState([]);
    const [open, setOpen] = useState(false);
    const [platforms, setPlatforms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        id: 0,
        studentId: 0,
        platform: "",
        profileUrl: "",
        usernameOrHandle: "",
        isActive: true,
        createdUserId: 0,
        modifiedUserId: 0,
    });
    const [redirectDialogOpen, setRedirectDialogOpen] = useState(false);
    const [selectedRedirectUrl, setSelectedRedirectUrl] = useState("");

    const navigate = useNavigate();

    const fetchLinks = async () => {
        try {
            const encrypted = localStorage.getItem("UserLogDetails");
            const decryptedUser = decryptText(encrypted);
            if (!decryptedUser) {
                safeToast.error("Session expired. Please login again.");
                navigate("/authentication/sign-in");
                return;
            }
            const { StudentId, Token } = JSON.parse(decryptedUser);
            
            // Fetch social media links
            const res = await axios.get(
                API_ENDPOINTS.PROFILE.GET_SOCIAL_MEDIA_LINKS(StudentId),
                {
                    headers: { Authorization: `Bearer ${Token}` },
                }
            );
            setSocialLinks(res.data);
            
            // Fetch platforms from dropdown
            const dropdownRes = await axios.get(
                API_ENDPOINTS.PROFILE.GET_DROPDOWNS,
                {
                    headers: { Authorization: `Bearer ${Token}` },
                }
            );
            
            // Extract platform names from dropdown data
            const platformList = (dropdownRes.data.socialMediaPlatforms || []).map(p => p.txt || p.ftxt).filter(Boolean);
            setPlatforms(platformList);
            
            setFormData((prev) => ({
                ...prev,
                studentId: StudentId,
                createdUserId: StudentId,
                modifiedUserId: StudentId,
            }));
        } catch (err) {
            console.error("Error fetching social links", err);
            safeToast.error("Failed to load social media links.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLinks();
    }, []);

    const handleOpen = (link = null) => {
        const encrypted = localStorage.getItem("UserLogDetails");
        const decryptedUser = decryptText(encrypted);
        const { StudentId } = JSON.parse(decryptedUser);
        if (link) {
            setFormData({ ...link });
        } else {
            setFormData({
                id: 0,
                studentId: StudentId,
                platform: "",
                profileUrl: "",
                usernameOrHandle: "",
                isActive: true,
                createdUserId: StudentId,
                modifiedUserId: StudentId,
            });
        }
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        try {
            const encrypted = localStorage.getItem("UserLogDetails");
            const decryptedUser = decryptText(encrypted);
            const { Token } = JSON.parse(decryptedUser);

            const res = await axios.post(
                API_ENDPOINTS.PROFILE.MERGE_SOCIAL_MEDIA_LINK,
                formData,
                {
                    headers: { Authorization: `Bearer ${Token}` },
                }
            );

            safeToast.success(res.data.message || "Social media link saved successfully!");
            setOpen(false);
            fetchLinks(); // Refresh list
        } catch (err) {
            console.error("Error saving social link", err);
            safeToast.error("Failed to save social media link.");
        }
    };

    const handleRedirectClick = (url) => {
        setSelectedRedirectUrl(url);
        setRedirectDialogOpen(true);
    };

    const confirmRedirect = () => {
        window.open(selectedRedirectUrl, "_blank");
        setRedirectDialogOpen(false);
    };

    const handleCopyUrl = async (url) => {
        try {
            await navigator.clipboard.writeText(url);
            safeToast.success("URL copied to clipboard!");
        } catch (err) {
            safeToast.error("Failed to copy URL.");
        }
    };

    return (
        <MDBox>
            <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
                <Box
                    sx={{
                        backgroundColor: "#1976d2",
                        color: "white",
                        px: 2,
                        py: 2.5,
                        borderTopLeftRadius: 8,
                        borderTopRightRadius: 8,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <MDTypography variant="h6" color="white">Social Media Links</MDTypography>
                    <Tooltip title="Add Link">
                        <IconButton onClick={() => handleOpen()}>
                            <Add sx={{ color: "#ffffff" }} />
                        </IconButton>
                    </Tooltip>
                </Box>
                <CardContent>
                    <Grid container spacing={2}>
                        {socialLinks.map((link) => (
                            <Grid item xs={12} key={link.id}>
                                <Card variant="outlined">
                                    <CardHeader
                                        avatar={iconMap[link.platform] || <Add />}
                                        title={<MDTypography variant="h6">{link.platform}</MDTypography>}
                                        action={
                                            <IconButton onClick={() => handleOpen(link)}>
                                                <Edit />
                                            </IconButton>
                                        }
                                    />
                                    <CardContent>
                                        <Grid container spacing={1} alignItems="center">
                                            <Grid item>
                                                <Person fontSize="small" />
                                            </Grid>
                                            <Grid item>
                                                <MDTypography variant="body2" fontWeight="medium">
                                                    {link.usernameOrHandle}
                                                </MDTypography>
                                            </Grid>
                                        </Grid>
                                        <Divider sx={{ my: 1 }} />
                                        <Grid container spacing={1} alignItems="center">
                                            <Grid item>
                                                <LinkIcon fontSize="small" />
                                            </Grid>
                                            <Grid item xs>
                                                <MDTypography
                                                    variant="body2"
                                                    color="textSecondary"
                                                    sx={{ cursor: "pointer", textDecoration: "underline" }}
                                                    title={link.profileUrl}
                                                    onClick={() => handleRedirectClick(link.profileUrl)}
                                                >
                                                    {link.profileUrl.length > 12 ? `${link.profileUrl.slice(0, 12)}...` : link.profileUrl}
                                                </MDTypography>
                                            </Grid>
                                            <Grid item>
                                                <Tooltip title="Copy URL">
                                                    <IconButton onClick={() => handleCopyUrl(link.profileUrl)}>
                                                        <ContentCopy fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>

            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle>{formData.id === 0 ? "Add" : "Edit"} Social Media Link</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Autocomplete
                                options={platforms}
                                value={formData.platform}
                                onChange={(e, newVal) => setFormData((prev) => ({ ...prev, platform: newVal }))}
                                getOptionLabel={(option) => option || ""}
                                renderOption={(props, option) => (
                                    <ListItem {...props}>
                                        <ListItemIcon>{iconMap[option] || <LinkIcon />}</ListItemIcon>
                                        <ListItemText primary={option} />
                                    </ListItem>
                                )}
                                renderInput={(params) => <TextField {...params} label="Platform" fullWidth />}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Profile URL"
                                name="profileUrl"
                                value={formData.profileUrl}
                                onChange={handleChange}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Username or Handle"
                                name="usernameOrHandle"
                                value={formData.usernameOrHandle}
                                onChange={handleChange}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={handleClose} color="secondary">
                        Cancel
                    </MDButton>
                    <MDButton onClick={handleSubmit} variant="gradient" color="info">
                        Save
                    </MDButton>
                </DialogActions>
            </Dialog>

            <Dialog open={redirectDialogOpen} onClose={() => setRedirectDialogOpen(false)}>
                <DialogTitle>Redirect Confirmation</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Do you want to open this profile URL in a new tab?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={() => setRedirectDialogOpen(false)} color="secondary">
                        Cancel
                    </MDButton>
                    <MDButton onClick={confirmRedirect} variant="gradient" color="info">
                        OK
                    </MDButton>
                </DialogActions>
            </Dialog>
        </MDBox>
    );
};

export default SocialMediaLinks;